import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { Box, Flex, Heading, IconButton, TabNav } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SectionsContext } from 'contexts/sections.context';
import { SectionName } from 'enums/route.enums';
import useOnScreen from 'hooks/useOnScreen';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo, useRef } from 'react';

const SCROLL_AMOUNT_PX = 300;

export const AdminTabNav = () => {
  const { adminSections, active, tryChangeSection } =
    useContext(SectionsContext);

  const tabsWrapper = useRef<HTMLDivElement>(null);

  const firstTab = useRef(null);
  const isFirstVisible = useOnScreen({
    element: firstTab.current,
  });

  const lastTab = useRef(null);
  const isLastVisible = useOnScreen({
    element: lastTab.current,
  });

  const visibleSections = useMemo(
    () =>
      adminSections
        ?.find((s) => s.value === SectionName.AdminPortal)
        ?.subsections?.filter((m) => !m.invisible) ?? [],
    [adminSections]
  );

  return (
    <ErrorBoundary componentName="AdminTabNav">
      <Flex direction="column" gap="2">
        <Heading mt="1" mb="0">
          {t('main.admin-portal')}
        </Heading>

        <div
          style={{
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            ref={tabsWrapper}
            style={{
              width: '100%',
              height: '52px',
              position: 'relative',
              overflowX: 'scroll',
              overflowY: 'hidden',
            }}
          >
            <TabNav.Root
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                // forces nav to take the full width if screen is wider than the tabs
                minWidth: '100%',
              }}
            >
              {visibleSections.map((m, i) => (
                <TabNav.Link
                  key={i}
                  ref={
                    i === 0
                      ? firstTab
                      : i === visibleSections.length - 1
                        ? lastTab
                        : undefined
                  }
                  active={active.subsection === m.value}
                  onClick={() =>
                    tryChangeSection({
                      section: SectionName.AdminPortal,
                      subsection: m.value,
                      trigger: 'AdminTabNav',
                    })
                  }
                >
                  <Flex gap={RADIX.FLEX.GAP.XS} align="center">
                    <Box>{t(m.label)}</Box>
                    {m.icon && (
                      <Box mt="1">
                        <m.icon />
                      </Box>
                    )}
                  </Flex>
                </TabNav.Link>
              ))}
            </TabNav.Root>
          </div>

          {!isFirstVisible && (
            <Flex
              align="center"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                // to match nav bar height
                height: '44px',
                width: '100px',
                backgroundImage:
                  'linear-gradient(to right, var(--color-background), 75%, rgba(0,0,0,0))',
              }}
            >
              <IconButton
                color={RADIX.COLOR.NEUTRAL}
                variant="soft"
                radius="full"
                onClick={() =>
                  tabsWrapper.current?.scrollBy({
                    left: -SCROLL_AMOUNT_PX,
                    behavior: 'smooth',
                  })
                }
              >
                <ArrowLeftIcon />
              </IconButton>
            </Flex>
          )}

          {!isLastVisible && (
            <Flex
              align="center"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                // to match nav bar height
                height: '44px',
                width: '100px',
                backgroundImage:
                  'linear-gradient(to left, var(--color-background), 75%, rgba(0,0,0,0))',
              }}
            >
              <IconButton
                color={RADIX.COLOR.NEUTRAL}
                variant="soft"
                radius="full"
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: 0,
                }}
                onClick={() =>
                  tabsWrapper.current?.scrollBy({
                    left: SCROLL_AMOUNT_PX,
                    behavior: 'smooth',
                  })
                }
              >
                <ArrowRightIcon />
              </IconButton>
            </Flex>
          )}
        </div>
      </Flex>
    </ErrorBoundary>
  );
};
